import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../services/supabase'
import mixpanelService from '../services/mixpanel'
import { profileService } from '../services/profileService'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Get initial session with error handling and timeout
    const initializeAuth = async () => {
      try {
        // Set a timeout for the session check
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session check timeout')), 15000)
        )
        
        const sessionPromise = supabase.auth.getSession()
        
        // Race between session check and timeout
        const { data: { session }, error } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ])
        
        if (error) {
          console.error('Session error:', error)
          // Clear potentially corrupted auth data
          await supabase.auth.signOut()
          setUser(null)
        } else {
          setUser(session?.user ?? null)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        // Clear auth data on timeout or other errors
        try {
          await supabase.auth.signOut()
        } catch (signOutError) {
          // If signOut fails, manually clear localStorage
          localStorage.removeItem('supabase.auth.token')
          localStorage.removeItem('sb-auth-token')
        }
        setUser(null)
      } finally {
        setLoading(false)
      }
    }
    
    initializeAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        try {
          const user = session?.user ?? null
          setUser(user)
          setLoading(false)
          
          // Handle token errors
          if (event === 'TOKEN_REFRESHED') {
            console.log('Token refreshed successfully')
          } else if (event === 'USER_UPDATED') {
            console.log('User updated')
          }
          
          // Track auth events
          if (event === 'SIGNED_IN' && user) {
            // Ensure profile exists
            await profileService.ensureProfile(user)
            
            mixpanelService.identify(user.id)
            mixpanelService.setUserProperties({
              email: user.email,
              created_at: user.created_at
            })
            mixpanelService.trackAuth('Login', user.app_metadata?.provider || 'email')
          } else if (event === 'SIGNED_OUT') {
            mixpanelService.trackAuth('Logout')
          }
        } catch (error) {
          console.error('Auth state change error:', error)
          // On any error, sign out to force re-authentication
          setUser(null)
          setLoading(false)
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    if (!error && data?.user) {
      try {
        mixpanelService.trackFunnelStep('signup', {
          method: 'email',
          user_id: data.user.id
        })
      } catch (e) {
        console.warn('Signup tracking failed:', e)
      }
    }
    return { data, error }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`
      }
    })
    return { data, error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const resetPassword = async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email)
    return { data, error }
  }

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}